import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useOpeningContext } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { PerformanceStatusBanner } from '../channels/performance-status-banner';
import { Chart } from './chart';
import { NoHiringGoal } from './no-hiring-goal';
import { NoMapping } from './no-mapping';
import { Stages } from './stages';

function FunnelContent() {
  const openingSlug = useOpeningSlug();
  const { showAggregateData, currentHiringGoalId } = useOpeningContext();

  const { data, refetch } = useSuspenseQuery({
    queryKey: [
      'openingDetailFunnel',
      openingSlug,
      currentHiringGoalId,
      showAggregateData,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingStageStats(
        openingSlug,
        currentHiringGoalId,
        showAggregateData,
      ),
  });

  const funnelData = useMemo(() => data?.sourcing_stages ?? [], [data]);

  const refetchFunnelData = useCallback(async () => {
    await refetch();
  }, [refetch]);

  if (funnelData.length === 0)
    return (
      <>
        <PerformanceStatusBanner />
        <NoMapping
          hiringGoalConversionRate={data?.hiring_goal_conversion_rate}
          refetchFunnelData={refetchFunnelData}
        />
      </>
    );

  return (
    <>
      <PerformanceStatusBanner />
      <div className="flex h-full max-h-full flex-1 gap-8 overflow-hidden pb-8">
        <Stages
          funnelData={funnelData}
          hiringGoalConversionRate={data?.hiring_goal_conversion_rate}
          lastAutomationTriggerAt={data?.last_automated_action_at}
          refetchFunnelData={refetchFunnelData}
        />
        <Chart funnelData={funnelData} />
      </div>
    </>
  );
}

export function Funnel() {
  return (
    <ErrorBoundary FallbackComponent={NoHiringGoal}>
      <Suspense fallback={<Funnel.Skeleton />}>
        <FunnelContent />
      </Suspense>
    </ErrorBoundary>
  );
}

Funnel.Skeleton = function FunnelSkeleton() {
  return (
    <div className="flex h-full max-h-full flex-1 gap-8 overflow-hidden pb-8">
      <Stages.Skeleton />
      <Chart.Skeleton />
    </div>
  );
};
